"use client";

import { useEffect, useId, useRef, useState, type ReactNode } from "react";

import { CcShowcaseHeightShell } from "@/components/services/shared/CcShowcaseHeightShell";
import { CcShowcasePillToggle } from "@/components/ui/CcShowcasePillToggle";

export type DemoTab = {
  key: string;
  label: string;
  summary: string;
  content: ReactNode;
};

type CcDemoTabsProps = {
  tabs: DemoTab[];
  tablistLabel: string;
  disclaimer: string;
};

/**
 * Step pills (CcShowcasePillToggle — same sliding control as Före/Efter) over a grey stage
 * with one app-style view per tab (webbyrå and AI visibility pages). Inert
 * hidden views; all views share one grid cell so the height stays stable.
 */
export function CcDemoTabs({ tabs, tablistLabel, disclaimer }: CcDemoTabsProps) {
  const baseId = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const [active, setActive] = useState(tabs[0]?.key ?? "");
  const demoRef = useRef<HTMLDivElement | null>(null);

  // Each view's single intro effect waits until the demo is on screen.
  useEffect(() => {
    const demo = demoRef.current;
    if (!demo) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        observer.disconnect();
        demo.dataset.inview = "true";
      },
      { threshold: 0.3 },
    );
    observer.observe(demo);
    return () => observer.disconnect();
  }, []);

  const tabId = (key: string) => `${baseId}-tab-${key}`;
  const panelId = (key: string) => `${baseId}-panel-${key}`;

  return (
    <div ref={demoRef} className="cc-demo">
      <div className="cc-demo__top">
        <CcShowcasePillToggle
          variant="tabs"
          className="cc-ai-showcase__toggle--demo"
          items={tabs.map((tab) => ({
            id: tab.key,
            label: tab.label,
            tabId: tabId(tab.key),
            controlsId: panelId(tab.key),
          }))}
          activeId={active}
          onSelect={setActive}
          ariaLabel={tablistLabel}
          scrollClassName="cc-demo__tabs-scroll"
        />
        <p className="cc-demo__disclaimer">{disclaimer}</p>
      </div>

      <CcShowcaseHeightShell
        shellClassName="cc-demo__stage-shell"
        innerClassName="cc-demo__stage"
        remeasureKey={active}
      >
        {tabs.map((tab) => (
          <div
            key={tab.key}
            id={panelId(tab.key)}
            role="tabpanel"
            aria-labelledby={tabId(tab.key)}
            className={`cc-demo__view cc-demo__view--${tab.key}`}
            data-active={active === tab.key}
            aria-hidden={active !== tab.key}
            inert={active !== tab.key}
          >
            <p className="cc-demo__summary">{tab.summary}</p>
            {tab.content}
          </div>
        ))}
      </CcShowcaseHeightShell>
    </div>
  );
}
